import { useState, useEffect, useCallback, useRef } from "react";
import { tasksAPI } from "../services/api";

// Custom hook for fetching task statistics (role-based)
export const useTaskStats = (page = 1, perPage = 50) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const hasFetchedRef = useRef(false);

  const userRole = localStorage.getItem("user_role");
  const user_id = localStorage.getItem("user_id");

  const fetchTaskStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response;

      // Use different endpoints based on user role
      if (userRole === "Admin") {
        // Admins can see all tasks
        response = await tasksAPI.getTaskStatistics();
        setTasks(response.tasks || []);
      } else if (userRole === "Annotator" || userRole === "Reviewer") {
        // For annotators and reviewers, use the new task statistics endpoint
        response = await tasksAPI.getUserTaskStatistics();
        setTasks(response.tasks || []);
      } else {
        throw new Error("Unauthorized access");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching task stats:", err);
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response;

      if (userRole === "Admin") {
        // Admins can see all tasks
        response = await tasksAPI.getTasks(page, perPage);
        setTasks(response.users || []); // Note: API returns 'users' field for tasks
        setPagination(response.pagination || {});
      } else if (userRole === "Annotator" || userRole === "Reviewer") {
        // For annotators and reviewers, use the task statistics endpoint
        response = await tasksAPI.getUserTaskStatistics();
        setTasks(response.tasks || []);
        setPagination({});
      } else {
        throw new Error("Unauthorized access");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [userRole, page, perPage]);

  useEffect(() => {
    // Prevent double fetching in StrictMode
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchTaskStats();
    }
  }, [fetchTaskStats]);

  return {
    tasks,
    pagination,
    loading,
    error,
    refetch: fetchTaskStats,
    fetchTasks,
  };
};

// Custom hook for task CRUD operations (role-based)
export const useTaskOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userRole = localStorage.getItem("user_role");

  const createTask = async (taskData) => {
    if (userRole !== "Admin") {
      throw new Error("Unauthorized: Only admins can create tasks");
    }

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.createTask(taskData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTask = async (taskId, taskData) => {
    if (userRole !== "Admin") {
      throw new Error("Unauthorized: Only admins can update tasks");
    }

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.updateTask(taskId, taskData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTask = async (taskId) => {
    if (userRole !== "Admin") {
      throw new Error("Unauthorized: Only admins can delete tasks");
    }

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.deleteTask(taskId);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const assignTask = async (assignmentData) => {
    if (userRole !== "Admin") {
      throw new Error("Unauthorized: Only admins can assign tasks");
    }

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.assignTask(assignmentData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const unassignTask = async (taskId) => {
    if (userRole !== "Admin") {
      throw new Error("Unauthorized: Only admins can unassign tasks");
    }

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.unassignTask(taskId);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyTask = async (taskId) => {
    if (userRole !== "Reviewer") {
      throw new Error("Unauthorized: Only reviewers can verify tasks");
    }

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.verifyTask(taskId);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyModel = async (modelId) => {
    if (userRole !== "Reviewer") {
      throw new Error("Unauthorized: Only reviewers can verify models");
    }

    setLoading(true);
    setError(null);
    try {
      const response = await tasksAPI.verifyModel(modelId);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createTask,
    updateTask,
    deleteTask,
    assignTask,
    unassignTask,
    verifyTask,
    verifyModel,
    loading,
    error,
    userRole,
  };
};
